import { api, ApiError } from './api.js';
import { el, fmtDate } from './ui.js';

export type PageStatus = { manual: boolean; stale: boolean; version: number };
type PageData = PageStatus & { text: string; history: Array<{ id: number; text: string | null; source: string; saved_at: string }> };

/** Una sola gestione per introduzioni del libro, temi e ritratti delle persone. */
export function pageEditor(kind: 'theme' | 'person', ref: string, text: string | null, status: PageStatus | undefined, refresh: () => Promise<void>): HTMLElement {
  const url = `/api/pages/${kind}/${encodeURIComponent(ref)}`;
  const section = el('section', { class: 'page-correction', 'aria-label': 'Correzioni del riassunto' });
  const initial = (): void => {
    section.replaceChildren(el('div', {},
      status?.manual ? el('p', { class: 'field-help' }, text ? 'Riassunto modificato da te · l’IA non lo riscrive.' : 'Hai tolto il riassunto · l’IA non lo ricrea.') : null,
      status?.stale ? el('p', { class: 'avviso' }, status.manual
        ? 'I racconti sono cambiati. Controlla anche il tuo riassunto: le tue parole sono rimaste intatte.'
        : 'I racconti sono stati corretti. Il vecchio riassunto automatico è nascosto in attesa di aggiornamento; puoi correggerlo tu adesso.') : null,
      el('button', { class: 'btn-secondario', click: async () => {
        const data = await api.get<PageData>(url);
        if (section.isConnected) edit(data);
      } }, text || status?.stale ? 'Modifica il riassunto' : 'Aggiungi o ripristina il riassunto'),
    ));
  };
  const edit = (data: PageData): void => {
    const inputId = `summary-${kind}-${ref.replace(/[^a-z0-9_-]/gi, '-')}`;
    const textarea = el('textarea', { id: inputId, class: 'scrivi-testo summary-textarea', rows: '12', maxlength: '50000', 'aria-describedby': `${inputId}-help` });
    textarea.value = data.text;
    const result = el('p', { class: 'esito', role: 'status', 'aria-live': 'polite' });
    const confirmation = el('div', { class: 'summary-confirmation' });
    const controls = el('fieldset', { class: 'summary-controls' });
    let saving = false;
    const save = async (next: string, revisionId?: number): Promise<void> => {
      if (saving) return;
      saving = true; controls.disabled = true; result.textContent = 'Salvo il riassunto…';
      try {
        if (revisionId !== undefined) await api.post(`${url}/restore`, { revisionId, version: data.version });
        else await api.put(url, { text: next, version: data.version });
        if (section.isConnected) await refresh();
      } catch (error) {
        result.textContent = error instanceof ApiError && error.status === 409
          ? 'Il riassunto o un racconto è cambiato mentre scrivevi. Le tue correzioni sono ancora qui: copiale, poi riapri l’editor per confrontarle con l’ultima versione.'
          : 'Non sono riuscita a salvare. Il testo è ancora qui: puoi riprovare.';
      } finally { saving = false; controls.disabled = false; }
    };
    const confirm = (message: string, action: () => Promise<void>): void => {
      confirmation.replaceChildren(el('p', {}, message), el('div', { class: 'correction-actions' },
        el('button', { class: 'btn-secondario', click: action }, 'Sì, conferma'),
        el('button', { class: 'btn-quieto', click: () => confirmation.replaceChildren() }, 'No, lascia così')));
      confirmation.querySelector('button')?.focus();
    };
    controls.append(el('div', {},
      el('label', { for: inputId }, 'Il testo del riassunto'),
      el('p', { class: 'field-help', id: `${inputId}-help` }, 'Puoi correggere, togliere o aggiungere frasi. Dopo il salvataggio l’IA non cambierà le tue parole. I racconti originali restano separati.'),
      textarea,
      el('div', { class: 'correction-actions' },
        el('button', { class: 'btn-verde', click: async () => {
          if (!textarea.value.trim()) confirm('Vuoi togliere il riassunto? Le storie restano. Potrai ripristinarlo dalle versioni precedenti.', () => save(''));
          else await save(textarea.value);
        } }, 'Salva il riassunto'),
        el('button', { class: 'btn-quieto', click: () => { initial(); section.querySelector('button')?.focus(); } }, 'Annulla'),
        data.text ? el('button', { class: 'btn-quieto', click: () => confirm('Vuoi togliere il riassunto? Le storie restano. Potrai ripristinarlo dalle versioni precedenti.', () => save('')) }, 'Togli il riassunto') : null,
      ), confirmation,
      data.history.length ? el('details', { class: 'summary-history' },
        el('summary', {}, 'Versioni precedenti'),
        ...data.history.map((revision) => el('div', { class: 'summary-revision' },
          el('p', { class: 'field-help' }, `${fmtDate(revision.saved_at)} · ${revision.source === 'manual' ? 'modifica manuale' : 'riassunto automatico'}`),
          el('p', { class: 'summary-preview' }, revision.text || '(Riassunto vuoto)'),
          el('button', { class: 'btn-secondario', click: () => confirm('Vuoi rimettere questa versione? Anche il riassunto attuale resterà nella cronologia.', () => save('', revision.id)) }, 'Ripristina questa versione'),
        ))) : null,
    ));
    section.replaceChildren(controls, result);
    textarea.focus();
  };
  initial();
  return section;
}
